import React from 'react';
import { ppEyebrowClass } from '../../patients/utils';
import type { PatientClinical } from '../../patients/types';
import { Icon, type IconKind } from '../Icon/Icon';
import { PatientProfileChip } from './PatientProfileChip';

function AlertGroup({
  icon,
  label,
  items,
  accent,
  critical,
}: {
  icon: IconKind;
  label: string;
  items: string[];
  accent: string;
  critical?: boolean;
}) {
  return (
    <div className="min-w-0">
      <div className="mb-[9px] flex items-center gap-[7px]">
        <Icon kind={icon} size={13} stroke={2} style={{ color: accent }} />
        <span className={ppEyebrowClass} style={{ color: 'var(--ink-3)' }}>
          {label}
        </span>
        <span className="font-[var(--font-mono)] text-[10px] font-bold text-[var(--ink-4)]">
          {items.length}
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it) => (
          <span
            key={it}
            className="inline-flex items-center gap-1.5 rounded-[var(--r-pill)] border bg-[var(--cream-1)] px-[11px] py-1 text-[12.5px] font-semibold text-[var(--ink-1)]"
            style={{
              borderColor: critical
                ? `color-mix(in srgb, ${accent} 38%, var(--cream-2))`
                : 'var(--cream-2)',
            }}
          >
            {critical ? (
              <span className="h-[5px] w-[5px] rounded-full" style={{ background: accent }} />
            ) : null}
            {it}
          </span>
        ))}
      </div>
    </div>
  );
}

export interface PatientClinicalAlertsProps {
  clinical: PatientClinical;
}

export function PatientClinicalAlerts({ clinical: c }: PatientClinicalAlertsProps) {
  return (
    <div className="overflow-hidden rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-0)]">
      <div className="flex items-center gap-[11px] border-b border-[var(--cream-2)] bg-[var(--cream-1)] px-[17px] py-[13px]">
        <PatientProfileChip icon="shield" accent="var(--terracotta)" />
        <span className="font-[var(--font-display)] text-[15.5px] font-semibold tracking-[-0.015em] text-[var(--ink-0)]">
          Clinical alerts
        </span>
        <span className={ppEyebrowClass} style={{ color: 'var(--ink-4)', marginLeft: 4 }}>
          Always shown
        </span>
      </div>
      <div className="grid gap-[18px] p-[16px_17px]">
        <AlertGroup
          icon="alert"
          label="Allergies"
          accent="var(--terracotta)"
          critical
          items={c.allergies}
        />
        <AlertGroup icon="activity" label="Conditions" accent="var(--gold)" items={c.conditions} />
        <AlertGroup icon="flask" label="Medications" accent="var(--ai-4)" items={c.medications} />
        <AlertGroup icon="eye" label="Be mindful of" accent="var(--gold)" items={c.mindful} />
      </div>
    </div>
  );
}
